import { z } from 'zod';
import {
  addressSchema,
  moneyStringSchema,
  positiveMoneySchema,
  requiredDeliveryAddressSchema,
  uuidSchema,
} from './common';

/**
 * Realtime Unilevel approve fields shared by product-level + per-variant
 * overrides. `rtuLevelShares` are independent percentages of the pool (each
 * 0–100, no sum constraint); `rtuLevels` must equal `rtuLevelShares.length`
 * (cross-checked in the service). Nullable = clear/inherit.
 */
const rtuApproveFields = {
  rtuCeilingPct: moneyStringSchema.nullable().optional(),
  rtuLevels: z.number().int().min(0).max(50).nullable().optional(),
  rtuLevelShares: z.array(moneyStringSchema).max(51).nullable().optional(),
};

/** A required, strictly-positive money-format dimension (weight / L / W / H). */
const positiveDimensionSchema = positiveMoneySchema;

// ── Public registration ──────────────────────────────────────────────────
// Registration is JSON (account + business profile + any TEXT requirement
// values). Requirement FILES are uploaded after login from the merchant
// dashboard (multipart), so a flaky upload never blocks account creation.
export const merchantRegisterSchema = z.object({
  email: z.string().email().max(200),
  password: z.string().min(8).max(200),
  firstName: z.string().min(1).max(120),
  middleName: z.string().max(120).optional(),
  lastName: z.string().min(1).max(120),
  // Contact # (personal), Business Contact #, Business Contact Email — all
  // required: couriers + the platform need a reachable merchant.
  phone: z.string().min(1).max(40),
  businessName: z.string().min(1).max(200),
  contactNumber: z.string().min(1).max(40),
  contactEmail: z.string().email().max(200),
  businessAddress: addressSchema.optional(),
  // FF delivery details (mirrors the Ecom address): every courier-required
  // field must be present. Stored on the merchant's `pickup_address` jsonb.
  pickupAddress: requiredDeliveryAddressSchema,
  pickupRegionCode: z.string().max(12).optional(),
  // Text-requirement values keyed by the tenant requirement `key`.
  requirements: z
    .array(z.object({ key: z.string().min(1).max(64), textValue: z.string().max(2000) }))
    .max(50)
    .optional(),
});
export type MerchantRegisterInput = z.infer<typeof merchantRegisterSchema>;

// ── Admin: create a merchant outright ─────────────────────────────────────
// Same account + business profile as self-registration, minus the requirement
// submissions (the admin is creating an already-vetted, pre-approved merchant,
// so there are no documents to review). The route creates the login user +
// merchant row with status `approved`.
export const adminMerchantCreateSchema = merchantRegisterSchema
  .omit({ requirements: true })
  .extend({
    // Admin-only: per-merchant platform commission % (REQUIRED — admin must enter
    // it, though 0 is allowed) + whether the merchant sees their earnings preview.
    // Clamped to the tenant fee band server-side.
    platformFeePct: moneyStringSchema,
    showFeeToMerchant: z.boolean().optional(),
  });
export type AdminMerchantCreateInput = z.infer<typeof adminMerchantCreateSchema>;

/**
 * Admin edit of an existing merchant's commission settings (admin-only). The
 * commission % is REQUIRED when present (no more blank = tenant default); `0` is
 * valid. `showFeeToMerchant` may be toggled on its own.
 */
export const adminMerchantUpdateSchema = z.object({
  platformFeePct: moneyStringSchema.optional(),
  showFeeToMerchant: z.boolean().optional(),
});
export type AdminMerchantUpdateInput = z.infer<typeof adminMerchantUpdateSchema>;

/**
 * Admin approve a pending merchant. Approval now persists the commission in the
 * same action (one primary button in the review dialog), so the fee % is
 * REQUIRED here (0 allowed); `showFeeToMerchant` is optional.
 */
export const adminMerchantApproveSchema = z.object({
  platformFeePct: moneyStringSchema,
  showFeeToMerchant: z.boolean().optional(),
});
export type AdminMerchantApproveInput = z.infer<typeof adminMerchantApproveSchema>;

// ── Merchant self-service ─────────────────────────────────────────────────
export const merchantProfileUpdateSchema = z.object({
  businessName: z.string().min(1).max(200).optional(),
  // Owner display name — applied to the linked account's `users.full_name`.
  ownerName: z.string().min(1).max(200).optional(),
  contactNumber: z.string().max(40).nullable().optional(),
  contactEmail: z.string().email().max(200).nullable().optional(),
  businessAddress: addressSchema.optional(),
  pickupAddress: addressSchema.optional(),
  pickupRegionCode: z.string().max(12).nullable().optional(),
});
export type MerchantProfileUpdateInput = z.infer<typeof merchantProfileUpdateSchema>;

/** Submit/replace a TEXT requirement value (files go through the multipart route). */
export const merchantRequirementSubmitSchema = z.object({
  requirementKey: z.string().min(1).max(64),
  textValue: z.string().max(2000),
});
export type MerchantRequirementSubmitInput = z.infer<typeof merchantRequirementSubmitSchema>;

// ── Merchant products ─────────────────────────────────────────────────────
// Merchant goods are cash-only marketplace items in v1: no kit, no per-currency
// pricing, no coin/binary/token grants. PV defaults to 0 (only relevant if the
// tenant turns `merchant_sales_earn_comp` on). SKU auto-generated when omitted.
// A product VARIATION the merchant submits. `price` is the variant's own sticker
// price; `stockQty` seeds merchant_variant_inventory. Fee/PV/VAT rates are NOT
// merchant-settable — the admin sets them per product (and optionally per
// variant) at review.
export const merchantVariantInputSchema = z.object({
  sku: z.string().min(1).max(64).optional(),
  name: z.string().min(1).max(200),
  attributes: z.record(z.string().max(120)).optional(),
  price: moneyStringSchema,
  // Compare-at / slashed price (SRP). When > price the storefront slashes it.
  srp: moneyStringSchema.nullable().optional(),
  stockQty: z.coerce.number().int().min(0).max(1_000_000).optional(),
  // Per-variant shipping overrides (null = inherit the product). No PV / binary
  // points / vortex — those are house-only.
  weightKg: moneyStringSchema.nullable().optional(),
  lengthCm: moneyStringSchema.nullable().optional(),
  widthCm: moneyStringSchema.nullable().optional(),
  heightCm: moneyStringSchema.nullable().optional(),
});
export type MerchantVariantInput = z.infer<typeof merchantVariantInputSchema>;

export const merchantProductCreateSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  // Rich long-form copy from the WYSIWYG Details tab (HTML; sanitized server-side).
  detailsHtml: z.string().max(20000).nullable().optional(),
  price: moneyStringSchema,
  // Compare-at / slashed SRP (the merchant's "before" price; price is what
  // members pay). When > price the storefront slashes it.
  srp: moneyStringSchema.nullable().optional(),
  pv: moneyStringSchema.optional(),
  sku: z.string().min(1).max(64).optional(),
  categoryId: uuidSchema.nullable().optional(),
  // Note: the platform fee % is NOT merchant-settable — admin sets it per
  // merchant (merchants.platform_fee_pct) and it's snapshotted at create.
  // Shipping dimensions are REQUIRED + positive — couriers quote on them, so a
  // product can't be submitted without weight + L×W×H. (A variant product's
  // variants inherit these unless they override per-variant.)
  weightKg: positiveDimensionSchema,
  lengthCm: positiveDimensionSchema,
  widthCm: positiveDimensionSchema,
  heightCm: positiveDimensionSchema,
  // Note: merchant goods are always ecom-only / sold-to-everyone. The channel
  // (platformSold) and buyer-eligibility (soldTo) are forced server-side at
  // create and are not merchant-settable, so they are intentionally absent here.
  // Initial on-hand stock for merchant_inventory (single-SKU products only —
  // ignored when `variants` is supplied; variant stock seeds per-variant).
  stockQty: z.coerce.number().int().min(0).max(1_000_000).optional(),
  // Optional variations. When present, the product is a variant product and its
  // purchasable units + stock live on the variants, not the product itself.
  variants: z.array(merchantVariantInputSchema).max(100).optional(),
});
export type MerchantProductCreateInput = z.infer<typeof merchantProductCreateSchema>;

// `sku` IS patchable — a merchant's catalog code is their own, and it used to be
// permanently the opaque auto-generated `M-…`. Reserved platform namespaces
// (`LEG-`, `KIT-`, `V-`, `M-`, the `-Archived` suffix) are rejected server-side.
export const merchantProductUpdateSchema = merchantProductCreateSchema
  .omit({ variants: true })
  .partial();
export type MerchantProductUpdateInput = z.infer<typeof merchantProductUpdateSchema>;

/** Edit an existing variant (price / name / sku / active). NULL fields untouched. */
export const merchantVariantUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  sku: z.string().min(1).max(64).optional(),
  attributes: z.record(z.string().max(120)).optional(),
  price: moneyStringSchema.optional(),
  srp: moneyStringSchema.nullable().optional(),
  active: z.boolean().optional(),
  weightKg: moneyStringSchema.nullable().optional(),
  lengthCm: moneyStringSchema.nullable().optional(),
  widthCm: moneyStringSchema.nullable().optional(),
  heightCm: moneyStringSchema.nullable().optional(),
});
export type MerchantVariantUpdateInput = z.infer<typeof merchantVariantUpdateSchema>;

export const merchantInventorySetSchema = z.object({
  qty: z.coerce.number().int().min(0).max(1_000_000),
});

// ── Merchant orders (buyer + fulfillment) ─────────────────────────────────
export const merchantOrderCreateSchema = z.object({
  merchantUserId: uuidSchema,
  items: z
    .array(
      z.object({
        productId: uuidSchema,
        // Set when buying a specific variation; omitted for single-SKU products.
        variantId: uuidSchema.optional(),
        qty: z.number().int().positive().max(10_000),
      }),
    )
    .min(1)
    .max(100),
  shipToAddress: addressSchema.optional(),
  shipToRegionCode: z.string().max(12).optional(),
  courierId: z.string().max(64).optional(),
  // Manual or cash-wallet method chosen at checkout; the server validates it
  // against the enabled ecom registry. Omitted = first enabled manual method.
  paymentMethodCode: z.string().max(64).optional(),
  // Which of the buyer's slots pays (wallet balances are per-slot). Ignored
  // unless it's one of their own active slots; falls back to their earliest.
  buyerSlotId: uuidSchema.optional(),
  // Single-payment correlation shared with the house order + sibling merchant
  // orders from the same checkout.
  checkoutGroupId: uuidSchema.optional(),
  notes: z.string().max(1000).optional(),
});
export type MerchantOrderCreateInput = z.infer<typeof merchantOrderCreateSchema>;

/** Merchant advances fulfillment on their own order. */
export const merchantOrderFulfillSchema = z.object({
  status: z.enum(['to_ship', 'shipping', 'delivered']),
  trackingNumber: z.string().max(120).optional(),
  note: z.string().max(500).optional(),
});

// ── Merchant payouts ──────────────────────────────────────────────────────
export const merchantPayoutRequestSchema = z.object({
  amount: moneyStringSchema,
  method: z.string().min(1).max(64),
  payload: z.record(z.unknown()).optional(),
});
export type MerchantPayoutRequestInput = z.infer<typeof merchantPayoutRequestSchema>;

// ── Admin: merchant lifecycle ─────────────────────────────────────────────
export const adminMerchantRejectSchema = z.object({
  reason: z.string().max(500).optional(),
});

export const adminMerchantRequirementReviewSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  rejectionReason: z.string().max(500).optional(),
});

/**
 * Approve a merchant product; the admin sets the pricing model rates that get
 * snapshotted onto each sale: platform fee %, PV-commission %, and the VAT
 * toggle/%. Per-variant overrides are keyed by variantId. Omitted fields keep
 * the product's current value (defaulted from tenant features at create).
 */
export const adminMerchantProductApproveSchema = z.object({
  platformFeePct: moneyStringSchema.optional(),
  pvCommissionPct: moneyStringSchema.optional(),
  vatEnabled: z.boolean().optional(),
  vatPct: moneyStringSchema.optional(),
  // Realtime Unilevel (product-level). Nullable = clear.
  ...rtuApproveFields,
  variantOverrides: z
    .array(
      z.object({
        variantId: uuidSchema,
        platformFeePct: moneyStringSchema.nullable().optional(),
        pvCommissionPct: moneyStringSchema.nullable().optional(),
        vatEnabled: z.boolean().nullable().optional(),
        vatPct: moneyStringSchema.nullable().optional(),
        ...rtuApproveFields,
      }),
    )
    .max(100)
    .optional(),
});
export const adminMerchantProductRejectSchema = z.object({
  rejectionReason: z.string().max(500).optional(),
});
